/**
 * Checks if a value is empty (undefined, null, empty string, or zero length)
 * @param {*} val - The value to check
 * @returns {boolean} True if the value is empty
 */
const valEmpty = (val) => {
    return val === undefined || val === null || val === '' || !val.length
}

/**
 * Validates if a value is within a list of options
 * @param {*} val - The value to validate
 * @param {Array} [options=[]] - The list of allowed options
 * @returns {boolean} True if the value is valid
 */
const optsValidator = (val, options = []) => {
    // Allow undefined/null/empty for optional props
    if (valEmpty(val)) return true
    return options.includes(val)
}

/**
 * Validates if all items in an array are within a list of options
 * @param {Array} [val=[]] - The array to validate
 * @param {Array} [options=[]] - The list of allowed options
 * @returns {boolean} True if all items are valid
 */
const optsArrayValidator = (val = [], options = []) => {
    // Allow undefined/null for optional props
    if (valEmpty(val)) return true
    return val.every((v) => options.includes(v))
}

/**
 * Validates if an object contains all required keys
 * @param {Object} val - The object to validate
 * @param {Array} [requiredKeys=[]] - The list of required keys
 * @returns {boolean} True if the object is valid
 */
const optsObjectValidator = (val, requiredKeys = []) => {
    // Allow undefined/null/empty for optional props
    if (valEmpty(val)) return true
    if (typeof val !== 'object') return false

    // Check if ALL required keys exist in the object
    return requiredKeys.every((key) => key in val)
}

/**
 * Validates an array of objects against a list of required keys
 * @param {Array} arr - The array of objects to validate
 * @param {Array} [requiredKeys=[]] - The list of required keys
 * @returns {boolean} True if all objects in the array are valid
 */
const optsArrayObjectValidator = (arr, requiredKeys = []) => {
    // Allow empty arrays
    if (!Array.isArray(arr) || arr.length === 0) return true

    // If array has items, validate each object
    return arr.every((item) => optsObjectValidator(item, requiredKeys))
}

/**
 * Button states and their associated colors and icons.
 */

const BTN_STATES = {
    default: {
        color: 'light-slate',
        icon: {
            name: '',
            type: 'secondary',
        },
    },
    completed: {
        color: 'green',
        icon: {
            name: 'fa-check',
            type: 'success',
        },
    },
    success: {
        color: 'green',
        icon: {
            name: 'fa-check',
            type: 'success',
        },
    },
    passed: {
        color: 'green',
        icon: {
            name: 'fa-circle-check',
            type: 'success',
        },
    },
    aborted: {
        color: 'red',
        icon: {
            name: 'fa-ban',
            type: 'destructive',
        },
    },
    aborting: {
        color: 'red',
        icon: {
            name: 'fa-spinner',
            type: 'destructive',
        },
    },
    destructive: {
        color: 'red',
        icon: {
            name: 'fa-circle-xmark',
            type: 'destructive',
        },
    },
    error: {
        color: 'red',
        icon: {
            name: 'fa-circle-xmark',
            type: 'destructive',
        },
    },
    failed: {
        color: 'red',
        icon: {
            name: 'fa-circle-xmark',
            type: 'destructive',
        },
    },
    'maximum reached': {
        color: 'red',
        icon: {
            name: 'fa-exclamation-circle',
            type: 'destructive',
        },
    },
    initialising: {
        color: 'blue',
        icon: {
            name: 'fa-circle-info',
            type: 'info',
        },
    },
    info: {
        color: 'blue',
        icon: {
            name: 'fa-circle-info',
            type: 'info',
        },
    },
    starting: {
        color: 'blue',
        icon: {
            name: 'fa-spinner',
            type: 'info',
        },
    },
    running: {
        color: 'blue',
        icon: {
            name: 'fa-spinner',
            type: 'info',
        },
    },
    loading: {
        color: 'blue',
        icon: {
            name: 'fa-spinner',
            type: 'info',
        },
    },
    paused: {
        color: 'dark-slate',
        icon: {
            name: 'fa-pause',
            type: 'light',
        },
    },
    pending: {
        color: 'dark-slate',
        icon: {
            name: 'fa-ellipsis',
            type: 'light',
        },
    },
    scheduled: {
        color: 'dark-slate',
        icon: {
            name: 'fa-calendar-check',
            type: 'light',
        },
    },
    queued: {
        color: 'dark-slate',
        icon: {
            name: 'fa-layer-group',
            type: 'light',
        },
    },
    unscheduled: {
        color: 'light-slate',
        icon: {
            name: 'fa-calendar-xmark',
            type: 'secondary',
        },
    },
    detached: {
        color: 'light-slate',
        icon: {
            name: 'fa-circle-minus',
            type: 'secondary',
        },
    },
    warning: {
        color: 'yellow',
        icon: {
            name: 'fa-triangle-exclamation',
            type: 'warning',
        },
    },
    'shield check': {
        color: 'green',
        icon: {
            name: 'fa-shield-check',
            type: 'success',
        },
    },
    'shield exclamation': {
        color: 'yellow',
        icon: {
            name: 'fa-shield-exclamation',
            type: 'warning',
        },
    },
    'shield xmark': {
        color: 'red',
        icon: {
            name: 'fa-shield-xmark',
            type: 'destructive',
        },
    },
    'shield slash': {
        color: 'green',
        icon: {
            name: 'fa-shield-slash',
            type: 'success',
        },
    },
}

const COLOR_TYPES = [
    'light',
    'success',
    'info',
    'warning',
    'destructive',
    'orange',
    'primary',
    'secondary',
    'disabled',
]

const COLOR_TO_TYPE = {
    white: 'light',
    green: 'success',
    'dark-green': 'success-dark',
    blue: 'info',
    'dark-blue': 'info-dark',
    yellow: 'warning',
    'dark-yellow': 'warning-dark',
    red: 'destructive',
    'dark-red': 'destructive-dark',
    orange: 'orange',
    'dark-orange': 'orange-dark',
    primary: 'primary',
    'primary-dark': 'primary-dark',
    'dark-slate': 'light',
    'light-slate': 'secondary',
}

const COLOR_OPTS = [
    'primary',
    'white',
    'black',
    'green',
    'light-green',
    'dark-green',
    'blue',
    'light-blue',
    'dark-blue',
    'yellow',
    'light-yellow',
    'dark-yellow',
    'red',
    'light-red',
    'dark-red',
    'orange',
    'light-orange',
    'dark-orange',
    'dark-slate',
    'light-slate',
    'destructive-dark',
]

const PADDING_SIZES = ['0', '1', '2', '3', '4', '5', '6', '7', '8']

const TOOLTIP_PLACEMENTS = [
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
]

const VULN_COLORS = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'green',
    info: 'blue',
}

export {
    optsValidator,
    optsArrayValidator,
    optsObjectValidator,
    optsArrayObjectValidator,
    BTN_STATES,
    COLOR_TYPES,
    COLOR_TO_TYPE,
    COLOR_OPTS,
    PADDING_SIZES,
    TOOLTIP_PLACEMENTS,
    VULN_COLORS,
}
